import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import TransactionCard from "@/components/TransactionCard";
import ExceptionDialog from "@/components/ExceptionDialog";
import { useTransactions, Column } from "@/lib/useTransactions";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Hand, PackageCheck, AlertTriangle } from "lucide-react";

const WH_EXCEPTIONS = ["Package not found", "Package still being located", "Package at another store",
  "Package not ready", "Package damaged", "System issue", "Customer information issue",
  "Supervisor assistance required", "Other"];

export default function Warehouse() {
  const { txns, reload } = useTransactions();
  const [excTxn, setExcTxn] = useState(null);
  const [busy, setBusy] = useState(null);

  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/transactions/${id}/${action}`);
      toast.success("Updated");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const waiting = txns.filter((t) => t.current_status === "WAITING");
  const accepted = txns.filter((t) => t.current_status === "ACCEPTED_BY_WAREHOUSE");
  const overdue = txns.filter((t) => t.is_overdue && !t.is_exception);
  const exceptions = txns.filter((t) => t.is_exception);

  return (
    <DashboardLayout title="Warehouse">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Column title="Waiting" count={waiting.length} accent="#F59E0B" testId="col-waiting">
          {waiting.map((t) => (
            <TransactionCard key={t.id} txn={t}>
              <Button size="sm" className="gap-1" disabled={busy === t.id} onClick={() => act(t.id, "accept")} data-testid={`accept-${t.kin_number}`}>
                <Hand size={14} /> Take Request
              </Button>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => setExcTxn(t)}><AlertTriangle size={14} /></Button>
            </TransactionCard>
          ))}
        </Column>

        <Column title="Preparing" count={accepted.length} accent="#3B82F6" testId="col-accepted">
          {accepted.map((t) => (
            <TransactionCard key={t.id} txn={t} assignedName={t.warehouse_accepted_by_name} subtitle={`Accepted`}>
              <Button size="sm" className="gap-1" disabled={busy === t.id} onClick={() => act(t.id, "prepare")} data-testid={`prepare-${t.kin_number}`}>
                <PackageCheck size={14} /> Mark Prepared
              </Button>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => setExcTxn(t)}><AlertTriangle size={14} /></Button>
            </TransactionCard>
          ))}
        </Column>

        <Column title="Overdue" count={overdue.length} accent="#FF3B30" testId="col-overdue">
          {overdue.map((t) => (
            <TransactionCard key={t.id} txn={t} assignedName={t.warehouse_accepted_by_name}>
              {t.current_status === "WAITING" ? (
                <Button size="sm" onClick={() => act(t.id, "accept")}>Take</Button>
              ) : (
                <Button size="sm" onClick={() => act(t.id, "prepare")}>Prepared</Button>
              )}
            </TransactionCard>
          ))}
        </Column>

        <Column title="Exceptions" count={exceptions.length} accent="#F59E0B" testId="col-exceptions">
          {exceptions.map((t) => <TransactionCard key={t.id} txn={t} />)}
        </Column>
      </div>

      {excTxn && (
        <ExceptionDialog open={!!excTxn} onOpenChange={(o) => !o && setExcTxn(null)} txn={excTxn}
          options={WH_EXCEPTIONS} onDone={reload} />
      )}
    </DashboardLayout>
  );
}
